import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchNormal from "@assets/svg/search-normal.svg?react";

import {
  FilterContainer,
  DropdownContainer,
  DropdownContent,
  OptionsContainer,
  SearchContainer,
  SearchInput,
  SearchIcon,
  OptionContainer,
  OptionContent,
  OptionText,
  StyledCheckbox,
} from "./styles";
import FilterDropDownBtn from "./FilterDropDownBtn";

export interface ChannelOption {
  key: string;
  label: string;
  icon: React.ReactElement;
}

interface ChannelFilterProps {
  label: string;
  enumOptions: string[];
  selectedValues: string[];
  onSelect: (keys: string[]) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const EnumFilter: React.FC<ChannelFilterProps> = ({
  label,
  enumOptions,
  selectedValues,
  onSelect,
  isOpen,
  onToggle,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredNames, setFilteredNames] = useState<string[]>(enumOptions);

  // Filter names based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredNames(enumOptions);
    } else {
      const filtered = enumOptions.filter((name) =>
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredNames(filtered);
    }
  }, [searchTerm, enumOptions]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Toggle channel selection
  const handleChannelClick = (key: string) => {
    let newSelected: string[];
    if (selectedValues.includes(key)) {
      newSelected = selectedValues.filter((k) => k !== key);
    } else {
      newSelected = [...selectedValues, key];
    }
    onSelect(newSelected);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Check if filter is active (has selected values)
  const isActive = selectedValues.length > 0;

  return (
    <FilterContainer ref={dropdownRef}>
      <FilterDropDownBtn label={label} onClick={onToggle} active={isActive} />
      {/* Dropdown content: Channel filter */}
      {isOpen && (
        <DropdownContainer>
          <DropdownContent>
            <OptionsContainer>
              {filteredNames.map((name: string) => {
                const checked = selectedValues.includes(name);
                return (
                  <OptionContainer
                    key={name}
                    onClick={() => handleChannelClick(name)}
                    checked={checked}
                  >
                    {/* Icon and text on the left */}
                    <OptionContent>
                      <OptionText checked={checked}>{name}</OptionText>
                    </OptionContent>
                    {/* MUI Checkbox on the right */}
                    <StyledCheckbox
                      checked={checked}
                      color="primary"
                      size="small"
                      onChange={() => handleChannelClick(name)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </OptionContainer>
                );
              })}
            </OptionsContainer>
          </DropdownContent>
        </DropdownContainer>
      )}
    </FilterContainer>
  );
};

export default EnumFilter;
